import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Header, Footer } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { api, formatApiError } from "@/lib/api";
import { toast, Toaster } from "sonner";
import ReactMarkdown from "react-markdown";
import {
  AlertTriangle,
  ArrowLeft,
  ArrowRight,
  Calendar,
  CheckCircle2,
  ClipboardPlus,
  HelpCircle,
  Loader2,
  Mic,
  MicOff,
  RefreshCw,
  Stethoscope,
  Users,
} from "lucide-react";

const POLL_MS = 2000;
const POLL_MAX = 90; // 3 min

const ESTADO_LABEL = {
  pendiente_pago: "Pendiente de pago",
  procesando: "Procesando",
  requiere_info: "Necesita más información",
  completada: "Completada",
  fallida: "Fallida",
  cancelada: "Cancelada",
};

const ESTADO_COLOR = {
  pendiente_pago: "bg-amber-50 border-amber-200 text-amber-900",
  procesando: "bg-blue-50 border-blue-200 text-blue-900",
  requiere_info: "bg-indigo-50 border-indigo-200 text-indigo-900",
  completada: "bg-emerald-50 border-emerald-200 text-emerald-900",
  fallida: "bg-red-50 border-red-200 text-red-900",
  cancelada: "bg-neutral-50 border-neutral-200 text-neutral-700",
};

const SpeechRecognitionCtor =
  typeof window !== "undefined"
    ? window.SpeechRecognition || window.webkitSpeechRecognition
    : null;

// Progress stages driven by real state + elapsed time (procesando).
function buildStages(detail, returnedFromStripe, elapsedProcSec) {
  const estado = detail?.estado || "pendiente_pago";
  const stages = [
    { key: "pago", label: "Confirmando tu pago con Stripe…", doneLabel: "Pago confirmado", done: false, active: false },
    { key: "historia", label: "Analizando tu historia clínica y síntomas…", doneLabel: "Contexto clínico analizado", done: false, active: false },
    { key: "gpc", label: "Consultando Guías de Práctica Clínica (GPC)…", doneLabel: "GPC de México aplicadas", done: false, active: false },
    { key: "respuesta", label: "Elaborando tu recomendación…", doneLabel: "Recomendación lista", done: false, active: false },
  ];
  if (estado === "pendiente_pago") {
    stages[0].active = returnedFromStripe;
    return stages;
  }
  if (estado === "procesando") {
    stages[0].done = true;
    // Simulate progressive stage completion based on elapsed time
    if (elapsedProcSec > 5) stages[1].done = true;
    else stages[1].active = true;
    if (elapsedProcSec > 15) stages[2].done = true;
    else if (elapsedProcSec > 5) stages[2].active = true;
    if (elapsedProcSec > 25) stages[3].active = true;
    return stages;
  }
  if (estado === "requiere_info" || estado === "completada") {
    stages.forEach((s) => (s.done = true));
    return stages;
  }
  return stages;
}

function ProgressStages({ stages, elapsedSec }) {
  return (
    <div className="rounded-xl border border-border bg-white p-5" data-testid="consulta-progress-panel">
      <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-3">
        Progreso {elapsedSec > 0 && <span className="ml-1 text-foreground/60">· {elapsedSec}s</span>}
      </div>
      <ol className="space-y-2.5">
        {stages.map((s) => (
          <li key={s.key} className="flex items-start gap-3" data-testid={`stage-${s.key}`}>
            <span className={
              "mt-0.5 grid place-items-center w-5 h-5 rounded-full text-[11px] font-medium shrink-0 " +
              (s.done
                ? "bg-emerald-100 text-emerald-700 border border-emerald-300"
                : s.active
                ? "bg-blue-100 text-blue-700 border border-blue-300"
                : "bg-neutral-100 text-neutral-400 border border-neutral-200")
            }>
              {s.done ? <CheckCircle2 size={12} /> : s.active ? <Loader2 size={12} className="animate-spin" /> : "•"}
            </span>
            <div className="flex-1">
              <div className={
                "text-sm " +
                (s.done ? "text-emerald-800" : s.active ? "text-[#0F2942] font-medium" : "text-muted-foreground")
              }>
                {s.done ? s.doneLabel : s.label}
              </div>
            </div>
          </li>
        ))}
      </ol>
    </div>
  );
}

function ClarificationForm({ preguntas, onSubmit, submitting, roundIdx, maxRounds }) {
  const [txt, setTxt] = useState("");
  const [listening, setListening] = useState(false);
  const recRef = useRef(null);
  const startVoice = () => {
    if (!SpeechRecognitionCtor) return toast.warning("Voz no soportada aquí.");
    const rec = new SpeechRecognitionCtor();
    rec.lang = "es-MX"; rec.continuous = true; rec.interimResults = true;
    rec.onstart = () => setListening(true);
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    rec.onresult = (evt) => {
      let finalT = "";
      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        if (evt.results[i].isFinal) finalT += evt.results[i][0].transcript;
      }
      if (finalT) setTxt((prev) => (prev ? `${prev} ${finalT}` : finalT).trim());
    };
    recRef.current = rec;
    try { rec.start(); } catch { /* ignore */ }
  };
  const stopVoice = () => { if (recRef.current) recRef.current.stop(); };
  return (
    <div className="rounded-xl border border-indigo-200 bg-indigo-50/30 p-5" data-testid="consulta-clarify-form">
      <div className="flex items-start gap-3 mb-3">
        <HelpCircle size={18} className="text-indigo-700 mt-0.5 shrink-0" />
        <div>
          <div className="font-semibold text-indigo-900">La IA necesita más información</div>
          <div className="text-xs text-indigo-800 mt-0.5">
            Ronda {roundIdx + 1} de {maxRounds}. Responde brevemente a las preguntas para obtener una recomendación segura.
          </div>
        </div>
      </div>
      <ul className="mb-3 space-y-1.5 pl-3 border-l-2 border-indigo-300">
        {preguntas.map((q, i) => (
          <li key={i} className="text-sm text-foreground" data-testid={`pregunta-${i}`}>
            <span className="text-indigo-700 font-mono mr-1">{i + 1}.</span> {q}
          </li>
        ))}
      </ul>
      <Textarea
        value={txt}
        onChange={(e) => setTxt(e.target.value)}
        placeholder="Escribe tus respuestas aquí, una por una…"
        rows={5}
        className="text-sm"
        data-testid="consulta-respuestas-input"
      />
      <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          {!listening ? (
            <Button variant="outline" size="sm" onClick={startVoice} data-testid="consulta-respuestas-mic-start">
              <Mic size={14} /> Dictar
            </Button>
          ) : (
            <Button variant="destructive" size="sm" onClick={stopVoice} data-testid="consulta-respuestas-mic-stop">
              <MicOff size={14} /> Detener
            </Button>
          )}
          <span className="text-[11px] text-muted-foreground">{txt.length} caracteres</span>
        </div>
        <Button
          onClick={() => onSubmit(txt.trim())}
          disabled={submitting || txt.trim().length < 3}
          className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white"
          data-testid="consulta-respuestas-submit"
        >
          {submitting ? <><Loader2 size={14} className="animate-spin" /> Enviando…</> : <>Enviar respuestas <ArrowRight size={12} /></>}
        </Button>
      </div>
    </div>
  );
}

export default function ConsultaDetalle() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [polling, setPolling] = useState(false);
  const [error, setError] = useState(null);
  const [submittingClarify, setSubmittingClarify] = useState(false);
  const [procStart, setProcStart] = useState(null);
  const [now, setNow] = useState(Date.now());

  const returnedFromStripe = !!params.get("session_id");

  const loadDetail = useCallback(async () => {
    try {
      const { data } = await api.get(`/consultas/${id}`);
      setDetail(data);
      return data;
    } catch (e) {
      setError(formatApiError(e));
      return null;
    }
  }, [id]);

  const pokeEstado = useCallback(async () => {
    try {
      await api.get(`/consultas/${id}/estado`);
    } catch {
      /* swallow; loadDetail surfaces */
    }
  }, [id]);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const d = await loadDetail();
      setLoading(false);
      if (d && (d.estado === "pendiente_pago" || d.estado === "procesando")) {
        setPolling(true);
        if (d.estado === "procesando") setProcStart(new Date(d.updated_at).getTime());
      }
    })();
  }, [loadDetail]);

  // Timer for progress display
  useEffect(() => {
    if (!procStart || detail?.estado !== "procesando") return;
    const t = setInterval(() => setNow(Date.now()), 500);
    return () => clearInterval(t);
  }, [procStart, detail?.estado]);

  useEffect(() => {
    if (!polling) return;
    let ticks = 0;
    let cancelled = false;
    const t = setInterval(async () => {
      ticks += 1;
      await pokeEstado();
      const d = await loadDetail();
      if (cancelled) return;
      if (!d) return;
      if (d.estado === "procesando" && !procStart) {
        setProcStart(new Date(d.updated_at).getTime());
      }
      if (["completada", "fallida", "cancelada", "requiere_info"].includes(d.estado)) {
        setPolling(false);
        if (d.estado === "completada") toast.success("Consulta procesada.");
        if (d.estado === "requiere_info") toast.message("La IA pide más información.");
        if (d.estado === "fallida") toast.error("La consulta falló al procesarse.");
      }
      if (ticks >= POLL_MAX) {
        setPolling(false);
      }
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [polling, pokeEstado, loadDetail, procStart]);

  const forceProcesar = async () => {
    try {
      await api.post(`/consultas/${id}/procesar`);
      toast.success("Solicitando procesamiento…");
      setPolling(true);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const enviarRespuestas = async (respuestas) => {
    setSubmittingClarify(true);
    try {
      await api.post(`/consultas/${id}/responder`, { respuestas });
      toast.success("Respuestas enviadas. Volviendo a analizar…");
      setProcStart(Date.now());
      setPolling(true);
      await loadDetail();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmittingClarify(false);
    }
  };

  if (loading) {
    return (
      <PageShell>
        <div className="text-sm text-muted-foreground flex items-center gap-2">
          <Loader2 className="animate-spin" size={14} /> Cargando consulta…
        </div>
      </PageShell>
    );
  }

  if (error && !detail) {
    return (
      <PageShell>
        <div className="rounded-md border border-destructive/30 bg-destructive/5 text-destructive p-4">
          {error}
        </div>
        <Button onClick={() => navigate("/dashboard")} className="mt-4">
          <ArrowLeft size={14} /> Volver al panel
        </Button>
      </PageShell>
    );
  }

  const isDerivacion =
    detail?.respuesta &&
    /derivar|directorio|urgencia|banderas? rojas?/i.test(detail.respuesta);

  const elapsedProcSec = procStart ? Math.floor((now - procStart) / 1000) : 0;
  const stages = buildStages(detail, returnedFromStripe, elapsedProcSec);
  const timeout = elapsedProcSec > 180; // 3 min

  return (
    <PageShell>
      <div className="mb-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-border bg-white px-3 py-1 text-[11px] font-medium text-[#0F2942]">
            <ClipboardPlus size={12} /> Consulta IA
          </div>
          <h1 className="mt-2 font-display text-3xl font-semibold text-[#0F2942] tracking-tight">
            Detalle de tu consulta
          </h1>
          <div className="text-xs text-muted-foreground mt-1 flex items-center gap-2">
            <Calendar size={12} />{" "}
            {detail ? new Date(detail.created_at).toLocaleString("es-MX") : ""}
          </div>
        </div>
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-medium ${
            ESTADO_COLOR[detail?.estado] || ESTADO_COLOR.pendiente_pago
          }`}
          data-testid={`consulta-estado-${detail?.estado}`}
        >
          {detail?.estado === "procesando" && <Loader2 size={12} className="animate-spin" />}
          {detail?.estado === "completada" && <CheckCircle2 size={12} />}
          {detail?.estado === "requiere_info" && <HelpCircle size={12} />}
          {ESTADO_LABEL[detail?.estado] || detail?.estado}
        </span>
      </div>

      {/* Progress stages — shown while pendiente_pago (returned from Stripe) or procesando */}
      {(detail?.estado === "pendiente_pago" || detail?.estado === "procesando") && (
        <div className="mb-4">
          <ProgressStages stages={stages} elapsedSec={elapsedProcSec} />
          {timeout && (
            <div className="mt-3 rounded-md border border-amber-300 bg-amber-50 text-amber-900 p-3 text-sm flex items-start gap-2" data-testid="consulta-timeout-notice">
              <AlertTriangle size={16} className="shrink-0 mt-0.5" />
              <div className="flex-1">
                <div className="font-semibold">Esto está tardando más de lo normal.</div>
                <div className="text-xs mt-0.5">Tu pago está seguro. Intenta reintentar el análisis.</div>
              </div>
              <Button variant="outline" size="sm" onClick={forceProcesar} data-testid="consulta-retry-btn-timeout">
                <RefreshCw size={12} /> Reintentar
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Explicit retry banner when pendiente_pago and NOT returned from Stripe */}
      {detail?.estado === "pendiente_pago" && !returnedFromStripe && (
        <div
          className="rounded-xl border border-amber-300 bg-amber-50 text-amber-900 p-4 mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
          data-testid="consulta-pending-banner"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle size={18} className="shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">Pago pendiente</div>
              <div className="text-xs mt-0.5">Aún no hemos recibido confirmación de Stripe.</div>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={forceProcesar} data-testid="consulta-retry-btn">
            <RefreshCw size={12} /> Reintentar
          </Button>
        </div>
      )}

      {/* Body */}
      <section className="grid gap-4">
        {detail?.sintomas && (
          <div className="rounded-xl border border-border bg-white p-5" data-testid="consulta-sintomas-panel">
            <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold mb-2">
              Síntomas enviados
            </div>
            <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
              {detail.sintomas}
            </p>
            {detail.laboratorio_ids?.length > 0 && (
              <div className="mt-3 text-xs text-muted-foreground">
                {detail.laboratorio_ids.length} laboratorio(s) adjuntos.
              </div>
            )}
          </div>
        )}

        {/* Requiere info — questions form */}
        {detail?.estado === "requiere_info" && detail?.preguntas?.length > 0 && (
          <ClarificationForm
            preguntas={detail.preguntas}
            onSubmit={enviarRespuestas}
            submitting={submittingClarify}
            roundIdx={detail.clarificacion_rounds || 0}
            maxRounds={detail.max_clarificacion_rounds || 2}
          />
        )}

        {detail?.estado === "completada" && detail?.respuesta && (
          <div className="rounded-xl border border-border bg-white p-5 sm:p-6" data-testid="consulta-respuesta-panel">
            <div className="flex items-center justify-between mb-2">
              <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-semibold">
                Respuesta clínica
              </div>
              {detail.provider_used && (
                <span className="text-[10px] font-mono text-muted-foreground">
                  {detail.provider_used}
                </span>
              )}
            </div>
            <article
              className="prose prose-sm max-w-none prose-headings:font-display prose-headings:text-[#0F2942] prose-strong:text-[#0F2942] prose-p:text-foreground prose-li:text-foreground"
              data-testid="consulta-respuesta-markdown"
            >
              <ReactMarkdown>{detail.respuesta}</ReactMarkdown>
            </article>

            {detail.gpc_codes?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {detail.gpc_codes.map((g) => (
                  <span
                    key={g}
                    className="inline-flex items-center gap-1 rounded-full border border-[#2563EB]/30 bg-[#2563EB]/8 text-[#0F2942] text-[11px] font-mono px-2 py-0.5"
                    data-testid={`consulta-gpc-${g}`}
                  >
                    <Stethoscope size={10} /> {g}
                  </span>
                ))}
              </div>
            )}

            {/* Disclaimer + derivation CTA */}
            <div className="mt-5 rounded-md border border-border bg-muted/40 p-3 text-xs text-muted-foreground">
              Esta orientación no sustituye una consulta médica profesional. Si notas empeoramiento
              o nuevos síntomas, acude a valoración presencial.
            </div>
            {isDerivacion && (
              <Link to="/directorio" data-testid="consulta-directorio-cta">
                <Button className="mt-3 bg-[#0F2942] hover:bg-[#123553] text-white">
                  <Users size={14} /> Ver Directorio de Especialistas <ArrowRight size={12} />
                </Button>
              </Link>
            )}
          </div>
        )}

        {detail?.estado === "fallida" && (
          <div className="rounded-xl border border-red-200 bg-red-50 text-red-900 p-4">
            La consulta no pudo procesarse. Contacta soporte si el problema persiste.
          </div>
        )}
      </section>

      <div className="mt-6 flex items-center justify-between">
        <Button variant="outline" onClick={() => navigate("/dashboard")} data-testid="consulta-back-dashboard-btn">
          <ArrowLeft size={14} /> Volver al panel
        </Button>
        <Link to="/consulta/nueva">
          <Button className="bg-[#2563EB] hover:bg-[#1e4fd0] text-white" data-testid="consulta-nueva-btn">
            Nueva consulta <ArrowRight size={12} />
          </Button>
        </Link>
      </div>
    </PageShell>
  );
}

function PageShell({ children }) {
  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />
      <main className="flex-1 max-w-3xl w-full mx-auto px-6 py-10">{children}</main>
      <Footer />
      <Toaster position="top-right" richColors />
    </div>
  );
}
